export interface LeaderboardEntry {
  id: string;
  playerName: string;
  score: number;
  level: number;
  monstersKilled: number;
  bossesKilled: number;
  timePlayed: number;
  earthType: string;
  completedAt: number;
  season?: string;
}

export interface LeaderboardConfig {
  id: string;
  name: string;
  description: string;
  icon: string;
  maxEntries: number;
  scoreType: 'level' | 'monsters' | 'time' | 'composite';
  sortOrder: 'desc' | 'asc';
}

export interface SeasonInfo {
  id: string;
  name: string;
  startDate: number;
  endDate: number;
  isActive: boolean;
  rewards?: {
    rank1: any;
    rank2: any;
    rank3: any;
    participation: any;
  };
}

export class LeaderboardManager {
  private static instance: LeaderboardManager;
  private leaderboards: Map<string, LeaderboardEntry[]> = new Map();
  private currentSeason: SeasonInfo | null = null;

  // Leaderboard configurations
  private static readonly LEADERBOARD_CONFIGS: LeaderboardConfig[] = [
    {
      id: 'highest_level',
      name: 'Highest Level',
      description: 'Players who reached the highest levels',
      icon: '📈',
      maxEntries: 50,
      scoreType: 'level',
      sortOrder: 'desc',
    },
    {
      id: 'monster_slayer',
      name: 'Monster Slayer',
      description: 'Most monsters killed in a single game',
      icon: '⚔️',
      maxEntries: 50,
      scoreType: 'monsters',
      sortOrder: 'desc',
    },
    {
      id: 'survival_time',
      name: 'Survival Master',
      description: 'Longest survival time',
      icon: '⏱️',
      maxEntries: 50,
      scoreType: 'time',
      sortOrder: 'desc',
    },
    {
      id: 'speed_run',
      name: 'Speed Runners',
      description: 'Fastest to reach level 20',
      icon: '⚡',
      maxEntries: 30,
      scoreType: 'time',
      sortOrder: 'asc',
    },
    {
      id: 'composite_score',
      name: 'Master Defenders',
      description: 'Overall best performance (composite score)',
      icon: '🏆',
      maxEntries: 100,
      scoreType: 'composite',
      sortOrder: 'desc',
    },
  ];

  private constructor() {
    this.initializeLeaderboards();
    this.loadLeaderboards();
    this.checkSeason();
  }

  public static getInstance(): LeaderboardManager {
    if (!LeaderboardManager.instance) {
      LeaderboardManager.instance = new LeaderboardManager();
    }
    return LeaderboardManager.instance;
  }

  private initializeLeaderboards(): void {
    LeaderboardManager.LEADERBOARD_CONFIGS.forEach(config => {
      this.leaderboards.set(config.id, []);
    });
  }

  private loadLeaderboards(): void {
    LeaderboardManager.LEADERBOARD_CONFIGS.forEach(config => {
      const saved = localStorage.getItem(`leaderboard_${config.id}`);
      if (saved) {
        try {
          const entries = JSON.parse(saved);
          this.leaderboards.set(config.id, entries);
        } catch (error) {
          console.error(`Error loading leaderboard ${config.id}:`, error);
          this.leaderboards.set(config.id, []);
        }
      }
    });
  }

  private saveLeaderboard(leaderboardId: string): void {
    const entries = this.leaderboards.get(leaderboardId);
    if (entries) {
      localStorage.setItem(`leaderboard_${leaderboardId}`, JSON.stringify(entries));
    }
  }

  private checkSeason(): void {
    // Check if we need to start a new season
    const saved = localStorage.getItem('currentSeason');
    if (saved) {
      this.currentSeason = JSON.parse(saved);
      
      // Check if season has ended
      if (this.currentSeason && Date.now() > this.currentSeason.endDate) {
        this.endSeason();
        this.startNewSeason();
      }
    } else {
      this.startNewSeason();
    }
  }

  private startNewSeason(): void {
    const now = Date.now();
    const seasonDuration = 30 * 24 * 60 * 60 * 1000; // 30 days
    
    this.currentSeason = {
      id: `season_${now}`,
      name: `Season ${this.getSeasonNumber()}`,
      startDate: now,
      endDate: now + seasonDuration,
      isActive: true,
      rewards: {
        rank1: { gold: 5000, ruby: 200, title: 'Season Champion' },
        rank2: { gold: 3000, ruby: 150, title: 'Season Runner-up' },
        rank3: { gold: 2000, ruby: 100, title: 'Season Bronze' },
        participation: { gold: 500, ruby: 25 },
      },
    };
    
    localStorage.setItem('currentSeason', JSON.stringify(this.currentSeason));
  }

  private endSeason(): void {
    if (!this.currentSeason) return;
    
    // Archive current season results
    const seasonResults = {
      season: this.currentSeason,
      leaderboards: this.getSeasonalLeaderboards(),
      endedAt: Date.now(),
    };
    
    // Save to season history
    const seasonHistory = JSON.parse(localStorage.getItem('seasonHistory') || '[]');
    seasonHistory.push(seasonResults);
    localStorage.setItem('seasonHistory', JSON.stringify(seasonHistory));
    
    // Distribute season rewards (in a real game, this would be done server-side)
    this.distributeSeasonRewards();
    
    this.currentSeason = null;
  }

  private distributeSeasonRewards(): void {
    // This is a simplified version - in a real game, rewards would be handled by the server
    console.log('Season ended - rewards would be distributed to top players');
  }

  private getSeasonNumber(): number {
    const history = JSON.parse(localStorage.getItem('seasonHistory') || '[]');
    return history.length + 1;
  }

  // Submit a new score to leaderboards
  public submitScore(gameData: {
    playerName: string;
    level: number;
    monstersKilled: number;
    bossesKilled: number;
    timePlayed: number;
    earthType: string;
    isSpeedRun?: boolean;
  }): { leaderboard: string; rank: number; isNewRecord: boolean }[] {
    const results: { leaderboard: string; rank: number; isNewRecord: boolean }[] = [];
    
    const entryId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const baseEntry: LeaderboardEntry = {
      id: entryId,
      playerName: gameData.playerName,
      score: 0, // Will be calculated per leaderboard
      level: gameData.level,
      monstersKilled: gameData.monstersKilled,
      bossesKilled: gameData.bossesKilled,
      timePlayed: gameData.timePlayed,
      earthType: gameData.earthType,
      completedAt: Date.now(),
      season: this.currentSeason?.id,
    };

    // Submit to relevant leaderboards
    LeaderboardManager.LEADERBOARD_CONFIGS.forEach(config => {
      let shouldSubmit = false;
      let score = 0;

      switch (config.id) {
        case 'highest_level':
          score = gameData.level;
          shouldSubmit = true;
          break;
          
        case 'monster_slayer':
          score = gameData.monstersKilled;
          shouldSubmit = gameData.monstersKilled > 0;
          break;
          
        case 'survival_time':
          score = gameData.timePlayed;
          shouldSubmit = gameData.timePlayed > 60000; // At least 1 minute
          break;
          
        case 'speed_run':
          score = gameData.timePlayed;
          shouldSubmit = !!(gameData.isSpeedRun && gameData.level >= 20);
          break;
          
        case 'composite_score':
          // Composite score: level * 1000 + monsters * 10 + bosses * 100 - (timePlayed / 1000)
          score = Math.round(
            gameData.level * 1000 + 
            gameData.monstersKilled * 10 + 
            gameData.bossesKilled * 100 - 
            (gameData.timePlayed / 1000)
          );
          shouldSubmit = gameData.level >= 5; // Minimum level 5 for composite
          break;
      }

      if (shouldSubmit) {
        const entry: LeaderboardEntry = { ...baseEntry, score };
        const result = this.addEntryToLeaderboard(config.id, entry);
        if (result) {
          results.push(result);
        }
      }
    });

    return results;
  }

  private addEntryToLeaderboard(leaderboardId: string, entry: LeaderboardEntry): { leaderboard: string; rank: number; isNewRecord: boolean } | null {
    const config = LeaderboardManager.LEADERBOARD_CONFIGS.find(c => c.id === leaderboardId);
    if (!config) return null;

    const leaderboard = this.leaderboards.get(leaderboardId) || [];
    
    // Check if this is a new record (better than current best)
    const isNewRecord = leaderboard.length === 0 || 
      (config.sortOrder === 'desc' && entry.score > (leaderboard[0]?.score ?? 0)) ||
      (config.sortOrder === 'asc' && entry.score < (leaderboard[0]?.score ?? 0));

    // Add entry
    leaderboard.push(entry);
    
    // Sort leaderboard
    leaderboard.sort((a, b) => {
      if (config.sortOrder === 'desc') {
        return b.score - a.score;
      } else {
        return a.score - b.score;
      }
    });
    
    // Trim to max entries
    if (leaderboard.length > config.maxEntries) {
      leaderboard.splice(config.maxEntries);
    }
    
    // Find rank
    const rank = leaderboard.findIndex(e => e.id === entry.id) + 1;
    
    // Save leaderboard
    this.leaderboards.set(leaderboardId, leaderboard);
    this.saveLeaderboard(leaderboardId);
    
    return { leaderboard: leaderboardId, rank, isNewRecord };
  }

  // Get leaderboard entries
  public getLeaderboard(leaderboardId: string, limit?: number): LeaderboardEntry[] {
    const leaderboard = this.leaderboards.get(leaderboardId) || [];
    return limit ? leaderboard.slice(0, limit) : [...leaderboard];
  }

  // Get all leaderboard configs
  public getLeaderboardConfigs(): LeaderboardConfig[] {
    return [...LeaderboardManager.LEADERBOARD_CONFIGS];
  }

  // Get player's rank in a specific leaderboard
  public getPlayerRank(leaderboardId: string, playerName: string): { rank: number; entry: LeaderboardEntry } | null {
    const leaderboard = this.leaderboards.get(leaderboardId) || [];
    for (let i = 0; i < leaderboard.length; i++) {
      if (leaderboard[i]?.playerName === playerName) {
        return { rank: i + 1, entry: leaderboard[i] as LeaderboardEntry };
      }
    }
    
    return null;
  }

  // Get player's best scores across all leaderboards
  public getPlayerBestScores(playerName: string): { [leaderboardId: string]: { rank: number; score: number; entry: LeaderboardEntry } } {
    const results: { [leaderboardId: string]: { rank: number; score: number; entry: LeaderboardEntry } } = {};
    
    LeaderboardManager.LEADERBOARD_CONFIGS.forEach(config => {
      const playerRank = this.getPlayerRank(config.id, playerName);
      if (playerRank) {
        results[config.id] = {
          rank: playerRank.rank,
          score: playerRank.entry.score,
          entry: playerRank.entry,
        };
      }
    });
    
    return results;
  }

  // Get current season info
  public getCurrentSeason(): SeasonInfo | null {
    return this.currentSeason;
  }

  // Get seasonal leaderboards (current season only)
  public getSeasonalLeaderboards(): { [leaderboardId: string]: LeaderboardEntry[] } {
    if (!this.currentSeason) return {};
    
    const seasonalBoards: { [leaderboardId: string]: LeaderboardEntry[] } = {};
    
    this.leaderboards.forEach((entries, leaderboardId) => {
      seasonalBoards[leaderboardId] = entries.filter(entry => 
        entry.season === this.currentSeason!.id
      );
    });
    
    return seasonalBoards;
  }

  // Get leaderboard statistics
  public getLeaderboardStats(leaderboardId: string): {
    totalEntries: number;
    averageScore: number;
    topScore: number;
    lastUpdated: number;
  } {
    const leaderboard = this.leaderboards.get(leaderboardId) || [];
    
    if (leaderboard.length === 0) {
      return {
        totalEntries: 0,
        averageScore: 0,
        topScore: 0,
        lastUpdated: 0,
      };
    }
    
    const totalScore = leaderboard.reduce((sum, entry) => sum + entry.score, 0);
    const averageScore = Math.round(totalScore / leaderboard.length);
    const topScore = leaderboard[0]?.score ?? 0;
    const lastUpdated = Math.max(...leaderboard.map(entry => entry.completedAt));
    
    return {
      totalEntries: leaderboard.length,
      averageScore,
      topScore,
      lastUpdated,
    };
  }

  // Clear all leaderboards (for testing)
  public clearLeaderboards(): void {
    this.leaderboards.clear();
    LeaderboardManager.LEADERBOARD_CONFIGS.forEach(config => {
      localStorage.removeItem(`leaderboard_${config.id}`);
      this.leaderboards.set(config.id, []);
    });
  }

  // Export leaderboards data
  public exportData(): string {
    const data = {
      leaderboards: Object.fromEntries(this.leaderboards),
      currentSeason: this.currentSeason,
      exportedAt: Date.now(),
    };
    
    return JSON.stringify(data, null, 2);
  }

  // Import leaderboards data
  public importData(jsonData: string): boolean {
    try {
      const data = JSON.parse(jsonData);
      
      if (data.leaderboards) {
        Object.entries(data.leaderboards).forEach(([leaderboardId, entries]) => {
          this.leaderboards.set(leaderboardId, entries as LeaderboardEntry[]);
          this.saveLeaderboard(leaderboardId);
        });
      }
      
      if (data.currentSeason) {
        this.currentSeason = data.currentSeason;
        localStorage.setItem('currentSeason', JSON.stringify(this.currentSeason));
      }
      
      return true;
    } catch (error) {
      console.error('Failed to import leaderboard data:', error);
      return false;
    }
  }

  // Get time remaining in current season
  public getSeasonTimeRemaining(): number {
    if (!this.currentSeason) return 0;
    return Math.max(0, this.currentSeason.endDate - Date.now());
  }

  // Format time for display
  public formatTime(milliseconds: number): string {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) {
      return `${days}d ${hours % 24}h`;
    } else if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  }
} 